import React, { useState } from 'react';
import { Container } from '../../components/layout/Container';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Textarea } from '../../components/common/Textarea';
import { Badge } from '../../components/common/Badge';
import { 
  useSendInvitations, 
  useReceivedInvitations, 
  useAcceptInvitation, 
  useDeclineInvitation,
  useResendInvitation,
  useSentInvitations,
  useDeleteInvitation,
  useAddCodeRaffle,
  useCodeRaffles,
  useRemoveCodeRaffle
} from '../../hooks/useInvitations';
import { useMyRaffles } from '../../hooks/useRaffles';
import { Invitation } from '../../types';
import { Select } from '../../components/common/Select';
import { formatDate } from '../../utils/formatters';
import { Copy, RefreshCw, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

export const Invitations: React.FC = () => {
  const [raffleId, setRaffleId] = useState('');
  const [emails, setEmails] = useState('');
  const [codeRaffleId, setCodeRaffleId] = useState('');
  const [accessCode, setAccessCode] = useState('');
  
  const { data: rafflesData } = useMyRaffles({ limit: 100 });
  const { data: receivedData } = useReceivedInvitations();
  const { data: sentData } = useSentInvitations({ raffleId: raffleId || undefined });
  const { data: codeRafflesData } = useCodeRaffles();
  const sendInvitations = useSendInvitations();
  const acceptInvitation = useAcceptInvitation();
  const declineInvitation = useDeclineInvitation();
  const resendInvitation = useResendInvitation();
  const deleteInvitation = useDeleteInvitation();
  const addCodeRaffle = useAddCodeRaffle();
  const removeCodeRaffle = useRemoveCodeRaffle();

  const raffles = rafflesData?.data?.items || [];
  const receivedInvitations = receivedData?.data || [];
  const sentInvitations = sentData?.data || [];
  const codeRaffles = codeRafflesData?.data || [];
  const codeBasedRaffles = raffles.filter(r => r.participationAccess === 'INVITE_CODE');

  const handleSend = async () => {
    if (!raffleId || !emails) return;
    await sendInvitations.mutateAsync({
      raffleId,
      emails: emails, // Send as comma-separated string
    });
    setEmails('');
  };

  const handleAddCodeRaffle = async () => {
    if (!codeRaffleId || !accessCode) return;
    await addCodeRaffle.mutateAsync({ raffleId: codeRaffleId, accessCode });
    setCodeRaffleId('');
    setAccessCode('');
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  return (
    <Container className="py-12">
      <h1 className="text-3xl font-bold mb-8">Invitations</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Send Email Invitations */}
        <div>
          <h2 className="text-2xl font-bold mb-4">Send Email Invitations</h2>
          <Card>
            <div className="space-y-4">
              <Select
                label="Select Raffle"
                value={raffleId}
                onChange={(e) => setRaffleId(e.target.value)}
                options={[
                  { value: '', label: 'Select a raffle' },
                  ...raffles.filter(r => r.participationAccess === 'INVITE_EMAIL').map(r => ({
                    value: r.id,
                    label: r.name,
                  })),
                ]}
              />
              <Textarea
                label="Email Addresses"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="email1@example.com, email2@example.com"
                helperText="Comma-separated email addresses (max 100)"
                rows={4}
              />
              <Button
                fullWidth
                onClick={handleSend}
                isLoading={sendInvitations.isPending}
                disabled={!raffleId || !emails}
              >
                Send Invitations
              </Button>
            </div>

            {/* Sent Invitations List */}
            {raffleId && sentInvitations.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="font-semibold mb-4">Sent Invitations</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {sentInvitations.map((invitation: Invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{invitation.email}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(invitation.createdAt)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={
                          invitation.status === 'ACCEPTED' ? 'success' :
                          invitation.status === 'DECLINED' ? 'danger' : 'warning'
                        }>
                          {invitation.status}
                        </Badge>
                        {invitation.status === 'PENDING' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => resendInvitation.mutateAsync(invitation.id)}
                            isLoading={resendInvitation.isPending}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteInvitation.mutateAsync(invitation.id)}
                          isLoading={deleteInvitation.isPending}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Received Invitations */}
        <div>
          <h2 className="text-2xl font-bold mb-4">Received Invitations</h2>
          <Card>
            {receivedInvitations.length === 0 ? (
              <p className="text-center text-gray-600 dark:text-gray-400 py-8">No invitations received</p>
            ) : (
              <div className="space-y-4">
                {receivedInvitations.map((invitation) => (
                  <div key={invitation.id} className="border-b last:border-b-0 pb-4 last:pb-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{invitation.raffle?.name}</h3>
                      <Badge variant={invitation.status === 'ACCEPTED' ? 'success' : 'warning'}>
                        {invitation.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{formatDate(invitation.createdAt)}</p>
                    {invitation.status === 'PENDING' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => acceptInvitation.mutateAsync(invitation.id)}
                          isLoading={acceptInvitation.isPending}
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => declineInvitation.mutateAsync(invitation.id)}
                          isLoading={declineInvitation.isPending}
                        >
                          Decline
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Code-Based Raffles Section */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* My Code-Based Raffles */}
        <div>
          <h2 className="text-2xl font-bold mb-4">My Code-Based Raffles</h2>
          <Card>
            {codeBasedRaffles.length === 0 ? (
              <p className="text-center text-gray-600 dark:text-gray-400 py-8">No code-based raffles</p>
            ) : (
              <div className="space-y-4">
                {codeBasedRaffles.map((raffle) => (
                  <div key={raffle.id} className="border-b last:border-b-0 pb-4 last:pb-0">
                    <h3 className="font-semibold mb-3">{raffle.name}</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Raffle ID</p>
                          <p className="text-sm font-mono">{raffle.id}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(raffle.id, 'Raffle ID')}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      {raffle.accessCode && (
                        <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Access Code</p>
                            <p className="text-sm font-mono">{raffle.accessCode}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyToClipboard(raffle.accessCode!, 'Access Code')}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Share the Raffle ID and Access Code with participants
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Join Code-Based Raffle */}
        <div>
          <h2 className="text-2xl font-bold mb-4">Join Code-Based Raffle</h2>
          <Card>
            <div className="space-y-4">
              <Input
                label="Raffle ID"
                value={codeRaffleId}
                onChange={(e) => setCodeRaffleId(e.target.value)}
                placeholder="Enter raffle ID"
              />
              <Input
                label="Access Code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="Enter access code"
              />
              <Button
                fullWidth
                onClick={handleAddCodeRaffle}
                isLoading={addCodeRaffle.isPending}
                disabled={!codeRaffleId || !accessCode}
              >
                Join Raffle
              </Button>
            </div>

            {/* Joined Code Raffles */}
            {codeRaffles.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="font-semibold mb-4">Joined Raffles</h3>
                <div className="space-y-3">
                  {codeRaffles.map((raffle) => (
                    <div key={raffle.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded">
                      <div className="flex-1">
                        <p className="font-medium">{raffle.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">ID: {raffle.id}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeCodeRaffle.mutateAsync(raffle.id)}
                        isLoading={removeCodeRaffle.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Container>
  );
};
